import {Injectable} from '@angular/core';
import {CadMarker} from "../../models/CadMarker";
import {BehaviorSubject, Observable, Subject} from "rxjs";
import {CesiumAdapterService} from "../cesiumAdapter21/cesium-adapter.service";
import {MapPosition} from "../../models/MapPosition";
import {HintManagerService} from "../hintManager/hint-manager.service";
import {fromPromise} from "rxjs/internal-compatibility";
import {HttpServiceService} from "../httpService/http-service.service";
import {HttpHeaders, HttpParams} from "@angular/common/http";
import {OverlayService} from "../overlay/overlay.service";
import {catchError, filter} from "rxjs/operators";
import {KeyListenersService} from "../keyListenersService/key-listeners.service";
import {Overlay} from "../overlay/Overlay";

@Injectable({
  providedIn: 'root'
})
export class AutoCadService {

  private createOverlayUrl = '/event/create-overlay/'+window['EVENT_ID'];
  private uploadOverlayImgUrl = 'event/overlay-image/'+window['EVENT_ID'];
  private previewOverlayId = null;
  private previewOverlayPosition = null;
  private isCadPlacingInProcess = false;
  private _activePendingOverlay: Overlay = null;
  private _markersSynchronized$ = new Subject<any>();
  private _isCadPlaced$ = new BehaviorSubject<boolean>(false);
  private _dragMarkerConfig = {entityId: null, position: null};
  private _markerPositionAccordingToOthers = null;
  get isCadPlaced$(){
    return this._isCadPlaced$.asObservable();
  }
  get markersSynchronized(){
    return this._markersSynchronized$.asObservable();
  }

  private markers: CadMarker[] = [];
  private imgMarkers: CadMarker[] = [];
  private markerIndex = 0;
  private loadedImages = [];
  private lastGeneratedCanvas:HTMLCanvasElement = null;
  private overlayPositions = null;
  private _fileOptions = null;
  get fileOptions() {
    return this._fileOptions;
  }
  set fileOptions(name: any){
   this._fileOptions = name;
  }
  // private markersToCalculateSync: CadMarker[] = [];
  private _markersUpdated = new Subject<CadMarker[]>();
  // private _markersPlaced$ = new Subject<CadMarker[]>();
  private cadOverlayPrimivitiveId:string | number;
  private _cadUrlChanged$ = new Subject();
  private _cadOpacityUpdated$ = new Subject();
  get cadOpacityUpdated():any{
    return this._cadOpacityUpdated$.asObservable();
  }
  // get markersPlaced(){
  //   return this._markersPlaced$.asObservable();
  // }
  private waitForMapMarkers = false;
  get markersUpdated(){
    return this._markersUpdated.asObservable();
  }
  _currentCADUrl = null;
  get currentCADUrl(){
    return this._currentCADUrl;
  }
  set currentCADUrl(value:string){
    this._cadUrlChanged$.next(value);
    this._currentCADUrl = value;
  }

  set activePendingOverlay(v: Overlay) {
    this._activePendingOverlay = v;
  }

  get cadUrlChanged$(){
    return this._cadUrlChanged$.asObservable();
  }

  private createOverlay$ = new Observable((observer)=>{
    let config = {
      name: this._activePendingOverlay ? this._activePendingOverlay.name : this.fileOptions.name,
      lt_lat: this.overlayPositions.left_top.latitude,
      lt_lng: this.overlayPositions.left_top.longitude,
      rb_lat: this.overlayPositions.right_bottom.latitude,
      rb_lng: this.overlayPositions.right_bottom.longitude,
      existing_id: this._activePendingOverlay ? this._activePendingOverlay.id : null
    };
    this.http.sendPostRequest(
      this.createOverlayUrl,
      new HttpParams()
        .set('config', JSON.stringify(config))
        .toString(),
      new HttpHeaders({
        'Content-Type': 'application/x-www-form-urlencoded'
      })).subscribe((result)=>{
      observer.next(result);
    }, (err)=>{
        observer.error(err)
    })
  });

  private _cadOpacity = null;
  get cadOpacity(){
    return this._cadOpacity;
  }

  set cadOpacity(value){
    this._cadOpacityUpdated$.next(value);
    this._cadOpacity = value;
    if (this._cadOpacity!=null){
      this.cesiumAdapter.setOverlayOpacity(this.cadOverlayPrimivitiveId, this._cadOpacity/100);
    }
  }
  constructor(
    private cesiumAdapter: CesiumAdapterService,
    private hintManager: HintManagerService,
    private http: HttpServiceService,
    private overlayService: OverlayService,
    private keyListeners: KeyListenersService
  ) {
    this.initListeners();
  }

  private removePreviewOverlay(){
    if (this.previewOverlayId!== null){
      this.cesiumAdapter.removeOverlay(this.previewOverlayId);
      this.previewOverlayId = null;
    }
  }

  clearPreviouslyImportedCad(){
    this.clearMarkers();
    this.removePreviewOverlay();
    this.activePendingOverlay = null;
    this.cadOpacity = null;
    this.previewOverlayPosition = null;
    if (this.cadOverlayPrimivitiveId!== null){
      this.cesiumAdapter.removeOverlay(this.cadOverlayPrimivitiveId);
      this.cadOverlayPrimivitiveId = null;
    }
  }

  private processEsc(){
    if (this.markers.length){
        if (confirm('Do you want to remove markers on CAD?')){
          this.clearMarkers();
          this.hintManager.isTimeoutHint = true;
          this.hintManager.hintText = 'Markers cleared, please place new markers or Esc to cancel';
        }
    }
    else if (this.previewOverlayId!=null || this.cadOverlayPrimivitiveId!=null){
      if (confirm('Do you want to remove CAD image?')){
        this.hintManager.clearHint();
        this.clearPreviouslyImportedCad();
      }
    }
  }

  private getMarkersAccording(marker:CadMarker){
    let otherMarkers = this.getAllMarkersExcept(marker);
    let resultObject = {};
    otherMarkers.forEach((_marker)=>{
      let x = _marker.mapPosition.longitude - marker.mapPosition.longitude;
      let y = _marker.mapPosition.latitude - marker.mapPosition.latitude;
      x = x/Math.abs(x);
      y = y/Math.abs(y);
      resultObject[_marker.id] = {x, y};
    });
    return resultObject;
  }

  private initListeners(){
    this.cesiumAdapter.clickOnMap.subscribe((position: MapPosition)=>{
      if(this.waitForMapMarkers && this.markers.length < 3){
        this.addMapPositionToMarker(position);
      }
    });

    this.keyListeners.keyDown$
      .pipe(filter(($event: KeyboardEvent) => !!$event.key))
      .subscribe((keyboardEvent: KeyboardEvent) => {
        if (
          keyboardEvent.key.toLowerCase() == 'escape' &&
          this.isCadPlacingInProcess
        ) {
          this.processEsc();
        }
      });

    this.cesiumAdapter.dragStart.subscribe((entityId)=>{
      let draggingMarker = this.getByEntityId(entityId);
      if (!draggingMarker){
        return;
      }
      this._markerPositionAccordingToOthers = this.getMarkersAccording(draggingMarker);
      if (this.cadOverlayPrimivitiveId){
        this._isCadPlaced$.next(true);
        // this.cesiumAdapter.removeOverlay(this.cadOverlayPrimivitiveId);
        // this.cadOverlayPrimivitiveId = null;
        this.cadOpacity = 70;
        // this.cesiumAdapter.showOverlay(this.previewOverlayId);
      }
      this._dragMarkerConfig.entityId = entityId;
      this._dragMarkerConfig.position = this.cesiumAdapter.getObjectPosition(entityId);
      this.cesiumAdapter.startDragMarker(entityId);
    });

    this.cesiumAdapter.dragEnd.subscribe((result:{position:MapPosition, id})=>{
      let draggingMarker = this.getByEntityId(result.id);
      if (!draggingMarker){
        return;
      }
      this.cesiumAdapter.removeOverlay(this.cadOverlayPrimivitiveId);
      this.cadOverlayPrimivitiveId = null;
      this.cadOpacity = null;
      delete result.position.height;
      draggingMarker.mapPosition = result.position;
      if (this.markers.length >=3){
        let markersAccording = this.getMarkersAccording(draggingMarker);
        let isDifferent = false;
        for (let i in markersAccording){
          if (markersAccording[i].x !== this._markerPositionAccordingToOthers[i].x || markersAccording[i].y !== this._markerPositionAccordingToOthers[i].y){
            isDifferent = true;
            break;
          }
        }

        if (isDifferent){
          alert('Wrong marker position. Image couldn\'t be generated with this markers position');
          this.setPreviousMarkerPosition(draggingMarker);
          return;
        }

        this.synchronizeMarkers().subscribe(()=>{
          this._dragMarkerConfig.entityId = null;
          this._dragMarkerConfig.position = null;
        }, (errorMessage)=>{
          alert(errorMessage);
          this.setPreviousMarkerPosition(draggingMarker);
        })
        // this._markersPlaced$.next(this.markers);
      }
      // this.synchronizeMarkers().subscribe(()=>{
      // });
    });

    this.cesiumAdapter.clickOnPrimitive$.subscribe(({primitiveId, clickPos})=>{
      if (primitiveId == this.previewOverlayId && this.markers.length < 3){
        let clickPositionCartesian3 = this.cesiumAdapter.getPositionFromWindowCoordinates(clickPos.x, clickPos.y);
        let cartographic = Cesium.Cartographic.fromCartesian(clickPositionCartesian3);
        let degreesPosition = {latitude: Cesium.Math.toDegrees(cartographic.latitude), longitude: Cesium.Math.toDegrees(cartographic.longitude)};
        this.addMarker({imgPosition: this.getPercentPosition(degreesPosition)});
        this.addMapPositionToMarker(new MapPosition(degreesPosition));
      }
    })
  }

  private setPreviousMarkerPosition(draggingMarker){
    let resultPos = this.cesiumAdapter.getDegreesFromCartesian(this._dragMarkerConfig.position);
    delete resultPos.height;
    draggingMarker.mapPosition = resultPos;
    this.cesiumAdapter.moveMarker(this._dragMarkerConfig.entityId, this._dragMarkerConfig.position, ()=>{
      this._dragMarkerConfig.entityId = null;
      this._dragMarkerConfig.position = null;
      this.synchronizeMarkers();
    })
  }

  private getPercentPosition({latitude, longitude}){
    let offset = {latitude: Math.abs(this.previewOverlayPosition.north - latitude), longitude: Math.abs(longitude - this.previewOverlayPosition.west)};
    let fullSize = {longitude: this.previewOverlayPosition.east - this.previewOverlayPosition.west, latitude: this.previewOverlayPosition.north - this.previewOverlayPosition.south};
    return {left: (offset.longitude / fullSize.longitude) *100, top: (offset.latitude / fullSize.latitude) *100};
  }

  private getByEntityId(entityId){
    return this.markers.find((marker:CadMarker)=>{return marker.entityId == entityId});
  }

  addMarker(config){
    if (!config.hasOwnProperty('id')){
      config.id = this.markers.length+1;
    }
    this.markers.push(new CadMarker(config));
    this._markersUpdated.next(this.markers);
  }

  clearMarkers(){
    this.markers.forEach((marker: CadMarker)=>{
      this.removeMarker(marker);
    });
    this.markers = [];
    this.imgMarkers.forEach((marker: CadMarker)=>{
      this.removeMarker(marker);
    });
    this.imgMarkers = [];
    this._markersUpdated.next([]);
    this.markerIndex = 0;
  }

  addMapPositionToMarker(markerPosition){
    let currentMarker = this.markers[this.markerIndex];
    currentMarker.mapPosition = markerPosition;
    this.cesiumAdapter.drawMarker(currentMarker, (id)=>{
      currentMarker.entityId = id;
    });
    this.markerIndex++;

    // if (this.markerIndex == this.markers.length){
    //   this.markerIndex = 0;
    //   this._markersPlaced$.next(this.markers);
    //   this.waitForMapMarkers = false;
    //   this.hintManager.clearHint();
    // }
  }

  private getLeftBottomMarker(img, markerList?):CadMarker{
    let minMarker: CadMarker;
    if (!markerList){
      markerList = this.markers;
    }
    let minVal:number = markerList[0].imgPosition.left;
    for (let i = 0; i< markerList.length; i++){
      let marker = markerList[i];
      //+marker.mapPosition.latitude
      let currentSum = marker.imgPosition.left;
      if (i == 0){
        minMarker = marker;
        minVal = currentSum;
        continue;
      }
      if (currentSum < minVal){
        minVal = currentSum;
        minMarker = marker;
      }
    }
    return minMarker;
  }

  private getAllMarkersExcept(marker, _markers?:CadMarker[]){
    let markers = this.markers;
    if (_markers){
      markers = _markers;
    }
    return markers.filter((_marker)=>{
      return _marker != marker;
    })
  }

  private isAngleGT90(marker:CadMarker, img){
    let otherMarkers = this.getAllMarkersExcept(marker);
    let A = this.getMarkerPxPosition(marker, img);
    let B = this.getMarkerPxPosition(otherMarkers[0], img);
    let C = this.getMarkerPxPosition(otherMarkers[1], img);


    let AB = Math.sqrt(Math.pow(B.left - A.left, 2) + Math.pow(B.top - A.top, 2));
    let AC = Math.sqrt(Math.pow(C.left - A.left, 2) + Math.pow(C.top - A.top, 2));
    let BC = Math.sqrt(Math.pow(C.left - B.left, 2) + Math.pow(C.top - B.top, 2));
    let cosBAC = ((AB*AB)+(AC*AC)-(BC*BC))/(2*AB*AC);

    //degrees

    let resultDegrees = Cesium.Math.toDegrees(Math.acos(cosBAC));
    if (resultDegrees >= 90){
      return true
    }
    return null;
  }

  public addPreviewCAD(){
    let viewPositions = this.cesiumAdapter.getViewRectangle();
    this.cesiumAdapter.drawOverlay(this.currentCADUrl, viewPositions.west, viewPositions.south, viewPositions.east, viewPositions.north, 1, 0, 0.01).then((overlayId: string | number) => {
      this.cesiumAdapter.setOverlayOpacity(overlayId, 0.7);
      this.hintManager.clearHint();
      this.previewOverlayId = overlayId;
      this.previewOverlayPosition = viewPositions;
      this.isCadPlacingInProcess = true;
    })
  }

  getDeltaValues(A,B,C,img){
    let aPx = this.getMarkerPxPosition(A, img);
    let bPx = this.getMarkerPxPosition(B, img);
    let cPx = this.getMarkerPxPosition(C, img);

    let AB = Math.sqrt(Math.pow(bPx.left - aPx.left, 2) + Math.pow(bPx.top - aPx.top, 2));
    //let AB2 = Math.sqrt(Math.pow(B.mapPosition.longitude - A.mapPosition.longitude, 2) + Math.pow(B.mapPosition.latitude - A.mapPosition.latitude, 2));
    let AC = Math.sqrt(Math.pow(cPx.left - aPx.left, 2) + Math.pow(cPx.top - aPx.top, 2));
    //let AC2 = Math.sqrt(Math.pow(C.mapPosition.longitude - A.mapPosition.longitude, 2) + Math.pow(C.mapPosition.latitude - A.mapPosition.latitude, 2));
    let OA = Math.abs(bPx.top - aPx.top);
    let OA2 = Math.abs(B.mapPosition.latitude - A.mapPosition.latitude);
    let OB = Math.abs(bPx.left - aPx.left);
    let OB2 = Math.abs(B.mapPosition.longitude - A.mapPosition.longitude);
    let AK = Math.abs(cPx.left - aPx.left);
    let AK2 = Math.abs(C.mapPosition.longitude - A.mapPosition.longitude);
    let CK = Math.abs(cPx.top - aPx.top);
    let sinB = OB / AB;
    let cosB = OA / AB;
    let sinC = AK / AC;
    let cosC = CK / AC;
    let alphaB = Cesium.Math.toDegrees(Math.acos(cosB));
    let alphaC = Cesium.Math.toDegrees(Math.acos(cosC));
    let SC = (C.mapPosition.longitude - A.mapPosition.longitude);
    let SB = (B.mapPosition.longitude - A.mapPosition.longitude);
    if (SB === 0) {SB = 1}
    if (SC === 0) {SC = 1}
    let S = (SC / SB)/Math.abs(SC/SB);
     // S = -1;
    let tgDeltaNumerator = AK2 * AB * sinB - S*OB2 * AC * sinC ;
    let tgDeltaDenominator = OB2 * AC * cosC - S*AK2 * AB* cosB;
    let tgDeltaAlpha = S*(tgDeltaNumerator / tgDeltaDenominator);
    let deltaAlpha = Cesium.Math.toDegrees(Math.atan(tgDeltaAlpha));
    let alphaBeforeChange = deltaAlpha;
    let bX = (B.mapPosition.longitude - A.mapPosition.longitude)/Math.abs(B.mapPosition.longitude - A.mapPosition.longitude);
    let bY = (B.mapPosition.latitude - A.mapPosition.latitude)/Math.abs(B.mapPosition.latitude - A.mapPosition.latitude);
    let maxVal = 360;
    if (deltaAlpha > 0){
      maxVal+=deltaAlpha;
    }
    do {
      if (deltaAlpha > 0){
        var newRotatedMarkersPos = this.getRotatedMarkersPositionToA([B, C], aPx, deltaAlpha, img);
        let newBX = newRotatedMarkersPos[0].left/Math.abs(newRotatedMarkersPos[0].left);
        let newBY = -newRotatedMarkersPos[0].top/Math.abs(newRotatedMarkersPos[0].top);
        if (newBX == bX && newBY == bY){
          break;
        }
      }
      deltaAlpha+=180;
    }
    while (deltaAlpha<=maxVal);
    if (deltaAlpha > maxVal){
      console.error('WE HAVE A PROBLEM!!!!!!!', deltaAlpha);
      return null;
    }
    let cosDeltaAlpha = Math.cos((deltaAlpha) * (Math.PI / 180));
    let sinDeltaAlpha = Math.sin((deltaAlpha) * (Math.PI / 180));
    let sinAlphaBDeltaAlpha = Math.abs(Math.sin((Cesium.Math.toRadians(alphaB+deltaAlpha))));
    let deltaY = Math.abs(B.mapPosition.latitude - A.mapPosition.latitude) / Math.abs(newRotatedMarkersPos[0].top);
    // let deltaY = (OA2 * cosB) / (OA * cosAlphaB);
    let deltaX = (AK2) / (AB * ((sinB*cosDeltaAlpha)+(cosB*sinDeltaAlpha)));
    return {deltaY, deltaX, deltaAlpha, alphaBeforeChange};
  }

  private processNormalizeImg(A, minMaxValues, canvas, otherMarkers, otherMarkersNewOffsetPosition){
    let newAPx = {left: -1*minMaxValues.min.x, top: -1*minMaxValues.min.y};

    A.imgPosition = {left: newAPx.left/canvas.width*100, top: newAPx.top/canvas.height*100};

    for (let i = 0; i < otherMarkers.length; i++) {
      let marker = otherMarkers[i];
      let newMarkerPxPos = {left: otherMarkersNewOffsetPosition[i].left + newAPx.left, top: otherMarkersNewOffsetPosition[i].top + newAPx.top};
      let newMarkerPercentPos = {left: newMarkerPxPos.left/canvas.width*100, top: newMarkerPxPos.top/canvas.height*100};
      marker.imgPosition = newMarkerPercentPos;
    }

  }

  /**
   *
   * @param A - left-bottom Marker
   * @param img
   * return rotation angle that need to set all dots in 1 cartesian part
   */
  private normalizeMarkers(A:CadMarker, img){
    return new Promise((resolve, reject)=>{
    let otherMarkers = this.getAllMarkersExcept(A);

    let aPx = this.getMarkerPxPosition(A, img);
    let otherMarkersNewOffsetPosition = [];
    let minAngle = null;

    for (let i = 0; i < otherMarkers.length; i++){
      let currentMarkerPos = this.getMarkerPxPosition(otherMarkers[i], img);
      let resultAngle = 90-(Math.atan2(aPx.top-currentMarkerPos.top, currentMarkerPos.left-aPx.left)*(180/Math.PI));
      if (resultAngle == 90){resultAngle = 0}

      if (minAngle == null || minAngle > resultAngle){
        minAngle = resultAngle;
      }
    }

    for (let i = 0; i < otherMarkers.length; i++) {
      let currentMarkerPos = this.getMarkerPxPosition(otherMarkers[i], img);
      let currentMarkerPosAccordingToA = {x: currentMarkerPos.left - aPx.left, y: currentMarkerPos.top - aPx.top};
      otherMarkersNewOffsetPosition.push(this.getRotatedDot(currentMarkerPosAccordingToA.x, currentMarkerPosAccordingToA.y,-minAngle));
    }

    let offsets = this.calculateRotatedDots(aPx, -minAngle, img);

    let minMaxValues = {
      min: {x: offsets.left_top.left, y: offsets.left_top.top},
      max: {x: offsets.left_top.left, y: offsets.left_top.top}
    };

    for (let i in offsets) {
      this.processMinMaxValues(minMaxValues, offsets[i]);
    }


    let canvas = this.getRotatedCanvas(aPx, minMaxValues, 0, img, this.getAllMarkersExcept(A), minAngle);
    let self = this;
    // canvas.toBlob((blob)=>{
      var newImg = document.createElement('img'),
        url = canvas.toDataURL('image/png');

      if (self.loadedImages.includes(url)){
        self.processNormalizeImg(A, minMaxValues, canvas, otherMarkers, otherMarkersNewOffsetPosition);
        resolve({newImg, url})
      }
      else{
        newImg.onload = function() {
          self.loadedImages.push(url);
          self.processNormalizeImg(A, minMaxValues, canvas, otherMarkers, otherMarkersNewOffsetPosition);
          resolve({newImg, url})
        };
      }
      newImg.src = url;
    // });

    // let canvasSize = {width: Math.abs(minMaxValues.min.x) + Math.abs(minMaxValues.max.x), height: Math.abs(minMaxValues.min.y) + Math.abs(minMaxValues.max.y)};
    // let canvasOffset = {x: Math.abs(minMaxValues.min.x) - Math.abs(aPx.left), y: Math.abs(minMaxValues.min.y) - Math.abs(aPx.top)};

    // return minAngle;
    })
  }

  private syncImageOnload(img, imageUrl, resolve, reject){
    let A = this.getLeftBottomMarker(img);
    let isAngleMoreThan90 = this.isAngleGT90(A, img);

    if (isAngleMoreThan90){
      let otherMarkers = this.getAllMarkersExcept(A);
      A = this.getLeftBottomMarker(img, otherMarkers);
    }

    if (!this.loadedImages.includes(imageUrl)){
      this.loadedImages.push(imageUrl);
    }
    document.querySelector('#es-cesium-container')['style'].cursor = 'wait';
    this.normalizeMarkers(A, img).then(({newImg, url}) => {
      img = newImg;

      img.onload = ()=>{};
      this.currentCADUrl = url;
      // A = this.getLeftBottomMarker(img);

      let averages = [];


      let markersToCalculate = this.getAllMarkersExcept(A);
      let aPx = this.getMarkerPxPosition(A, img);
      let deltaVal = this.getDeltaValues(A, markersToCalculate[0], markersToCalculate[1], img);
      if (!deltaVal){
        reject('Wrong marker position. Image couldn\'t be generated with this markers position');
        document.querySelector('#es-cesium-container')['style'].cursor = 'auto';
        return;
      }
      averages.push(deltaVal);


      let _deltaAlpha = 0;
      let _deltaX = 0;
      let _deltaY = 0;
      let alphaBeforeChange = 0;

      averages.forEach(({deltaY, deltaX, deltaAlpha, beforeAlpha}) => {
        _deltaAlpha += deltaAlpha;
        _deltaX += deltaX;
        _deltaY += deltaY;
        alphaBeforeChange += beforeAlpha;
      });

      let deltaAlpha = _deltaAlpha / averages.length;
      let deltaX = _deltaX / averages.length;
      let deltaY = _deltaY / averages.length;


      let dotsOffset = this.calculateRotatedDots(aPx, deltaAlpha, img);

      console.log('$dotsOffset', dotsOffset, aPx, deltaAlpha, img.width, img.height);

      let minMaxValues = {
        min: {x: dotsOffset.left_top.left, y: dotsOffset.left_top.top},
        max: {x: dotsOffset.left_top.left, y: dotsOffset.left_top.top}
      };


      for (let i in dotsOffset) {
        this.processMinMaxValues(minMaxValues, dotsOffset[i]);
      }

      console.log('processMinMaxValues', minMaxValues);
      let resultCanvas = this.getRotatedCanvas(aPx, minMaxValues, deltaAlpha, img, markersToCalculate, 0);
      this.lastGeneratedCanvas = resultCanvas;

      let newRotatedMarkersPos = this.getRotatedMarkersPositionToA(markersToCalculate, aPx, deltaAlpha, img);
      console.log('newRotatedMarkersPos', newRotatedMarkersPos);

      let calcDeltaX = Math.abs(markersToCalculate[0].mapPosition.longitude - A.mapPosition.longitude) / Math.abs(newRotatedMarkersPos[0].left);
      let calcDeltaY = Math.abs(markersToCalculate[0].mapPosition.latitude - A.mapPosition.latitude) / Math.abs(newRotatedMarkersPos[0].top);


      let mapPosition = this.getImageMapPosition(calcDeltaX, calcDeltaY, A, minMaxValues);
      this.overlayPositions = mapPosition;
      console.log('mapPosition', mapPosition);
      this.imgMarkers.forEach((marker) => {
        this.removeMarker(marker);
      });
      this.imgMarkers = [];

      let imgSrc = null;
      // resultCanvas.toBlob((blob) => {
        var newImg:any = document.createElement('img'),
          url:any = resultCanvas.toDataURL('image/png');

        let self = this;
        newImg.onload = function () {
          self.cesiumAdapter.drawOverlay(imgSrc, mapPosition.left_top.longitude, mapPosition.right_bottom.latitude, mapPosition.right_bottom.longitude, mapPosition.left_top.latitude, 1, 0, 0.01).then((result: string | number) => {
            self.cadOpacity = 100;
            self.cadOverlayPrimivitiveId = result;
            resolve(self.cadOverlayPrimivitiveId);
            document.querySelector('#es-cesium-container')['style'].cursor = 'auto';
            self._markersSynchronized$.next(self.cadOverlayPrimivitiveId);
            self._isCadPlaced$.next(true);
            if (self.previewOverlayId!=null) {
              self.removePreviewOverlay();
            }
            // URL.revokeObjectURL(url);
          });
        };
        newImg.src = url;
        imgSrc = url;
      // });

    });
  }

  synchronizeMarkers() {
    return fromPromise(new Promise((resolve, reject) => {
      let imageUrl = this.currentCADUrl;
      let img: any = new Image();
      img.src = imageUrl;
      let self = this;
      img.onload = () => {
        self.syncImageOnload(img, imageUrl, resolve, reject);
      }
    }));
  }

  enableMapMarkersProcessStatus(){
    this.waitForMapMarkers = true;
    this.hintManager.isTimeoutHint = false;
    this.hintManager.textColor = '#fb5915';
    this.hintManager.hintText = "Please put 3 Markers in the map. Use the same positions and order as in image.<br/>" +
      "Click the map to create a Marker.";
  }


  private getRotatedMarkersPositionToA(markersToCalculate, markerPx, alpha, img):{left, top}[]{
    return markersToCalculate.map((_marker)=>{
      let markerPxPos = this.getMarkerPxPosition(_marker, img);
      markerPxPos.left -= markerPx.left;
      markerPxPos.top -= markerPx.top;
      return this.getRotatedDot(markerPxPos.left, markerPxPos.top, alpha);
    });
  }

  //synchronize markers methods
  private getImageMapPosition(deltaX, deltaY, marker, minMaxValues){
    console.log('getImageMapPosition', minMaxValues, deltaX, deltaY);
    let left_top = {longitude: marker.mapPosition.longitude - (Math.abs(minMaxValues.min.x)*deltaX), latitude: marker.mapPosition.latitude + (Math.abs(minMaxValues.min.y)*deltaY)};
    let right_bottom = {longitude: marker.mapPosition.longitude + (Math.abs(minMaxValues.max.x)*deltaX), latitude:marker.mapPosition.latitude - (Math.abs(minMaxValues.max.y)*deltaY)};
    return {left_top, right_bottom};
  }

  private getRotatedCanvas(markerPx, minMaxValues, alpha, img, markersToCalculate, additionalRotateAngle){
    console.log('getRotatedCanvas', arguments);
    let canvasSize = {width: Math.abs(minMaxValues.min.x) + Math.abs(minMaxValues.max.x), height: Math.abs(minMaxValues.min.y) + Math.abs(minMaxValues.max.y)};
    let canvasOffset = {x: Math.abs(minMaxValues.min.x) - Math.abs(markerPx.left), y: Math.abs(minMaxValues.min.y) - Math.abs(markerPx.top)};

    let newRotatedMarkersPos = this.getRotatedMarkersPositionToA(markersToCalculate, markerPx, alpha-additionalRotateAngle, img);

    var tmpCanvas = document.createElement('canvas');
    var tmpCtx = tmpCanvas.getContext('2d');

    var ratio = 1;
    if (canvasSize.width > 11190 || canvasSize.height > 11190) {
      ratio = Math.max(canvasSize.width / 11190, canvasSize.height / 11190);
    }

    tmpCanvas.width = canvasSize.width / ratio;
    tmpCanvas.height = canvasSize.height / ratio;

    tmpCtx.translate((canvasOffset.x+markerPx.left) / ratio, (canvasOffset.y+markerPx.top) / ratio);
    tmpCtx.save();
    tmpCtx.rotate((alpha-additionalRotateAngle)/180*Math.PI);
    tmpCtx.translate(-1*(markerPx.left) / ratio, -1*(markerPx.top) / ratio);
    tmpCtx.drawImage(img, 0, 0, img.width / ratio, img.height / ratio);
    tmpCtx.restore();
    // newRotatedMarkersPos.push({left: 0, top: 0});
    // for (let i = 0; i < newRotatedMarkersPos.length; i++){
    //   tmpCtx.beginPath();
    //   tmpCtx.fillStyle = 'blue';
    //   tmpCtx.arc(newRotatedMarkersPos[i].left, newRotatedMarkersPos[i].top, 2, 0, 2 * Math.PI);
    //   // tmpCtx.fillStyle = 'red';
    //   // tmpCtx.fillRect(newRotatedMarkersPos[i].left, newRotatedMarkersPos[i].top, 100, 100);
    //   tmpCtx.fill();
    //   tmpCtx.closePath();
    // }

    return tmpCanvas;
  }



  private calculateRotatedDots(markerPx, alpha, img){
    let dotsOffset = {left_top: null, right_top: null, right_bottom: null, left_bottom: null};
    dotsOffset.left_top = this.getRotatedDot(-1*markerPx.left, -1*markerPx.top, alpha);
    dotsOffset.right_top = this.getRotatedDot(img.width-markerPx.left, -1*markerPx.top, alpha);
    dotsOffset.right_bottom = this.getRotatedDot(img.width-markerPx.left, img.height-markerPx.top, alpha);
    dotsOffset.left_bottom = this.getRotatedDot(-1*markerPx.left, img.height-markerPx.top, alpha);


    return dotsOffset;
  }

  private processMinMaxValues(minMax, dotOffset){
    if (minMax.min.x > dotOffset.left){minMax.min.x = dotOffset.left}
    if (minMax.max.x < dotOffset.left){minMax.max.x = dotOffset.left}
    if (minMax.min.y > dotOffset.top){minMax.min.y = dotOffset.top}
    if (minMax.max.y < dotOffset.top){minMax.max.y = dotOffset.top}
  }

  private getRotatedDot(x, y, alpha){
    let left = x*Math.cos(Cesium.Math.toRadians(alpha)) - y*Math.sin(Cesium.Math.toRadians(alpha));
    let top = x*Math.sin(Cesium.Math.toRadians(alpha)) + y*Math.cos(Cesium.Math.toRadians(alpha));
    return {left, top}
  }

  private getMarkerPxPosition(marker, img){
    return {left: marker.imgPosition.left/100*img.width, top: marker.imgPosition.top/100*img.height};
  }

  public removeMarker(marker: CadMarker){
    this.cesiumAdapter.removeObject(marker.entityId);
  }

  private sendImagePart(overlayId, data: string, finalize?){
    return fromPromise(new Promise((resolve, reject)=>{
      let config:any = {oid: overlayId, data: data};
      if (finalize){
        config.finalize = finalize;
      }
      this.http.sendPostRequest(
        this.uploadOverlayImgUrl,
        new HttpParams()
          .set('config', JSON.stringify(config))
          .toString(),
        new HttpHeaders({
          'Content-Type': 'application/x-www-form-urlencoded'
        })).subscribe((result)=>{
          resolve(result);
      })
    }))
  }

  private processCadUpload(overlayId, blob, blobSize, stepSize, currentSize, nextSize, cb){
    // nextSize+=stepSize;
    nextSize = Math.min(currentSize + stepSize, blobSize);
    // if (nextSize > blobSize){ nextSize -= (nextSize - blobSize); }
    let chunk:Blob = blob.slice(currentSize, nextSize);
    let reader = new FileReader();
    reader.readAsDataURL(chunk);

    reader.onload = (result)=>{
      let data = result.target['result'];
      data = data.substring(data.indexOf(',')+1);
      let isFinalize = nextSize >= blobSize;
      this.sendImagePart(overlayId, data, isFinalize).subscribe((result)=>{
        if (isFinalize){
          cb(result);
        }
        else{
          currentSize=nextSize;
          this.processCadUpload(overlayId, blob, blobSize, stepSize, currentSize, nextSize, cb)
        }
      });
    }
  }

  private saveCadImg(overlayId, cb){
    this.lastGeneratedCanvas.toBlob((blob)=>{
      let blobSize = blob.size;
      let stepSize =  512000;
      let currentSize = 0;
      let nextSize = 0;
      this.processCadUpload(overlayId, blob, blobSize, stepSize, currentSize, nextSize, cb);
    });

  }

  public saveCadToServer(){
    return fromPromise(new Promise((resolve, reject)=>{
      this.createOverlay$.subscribe((overlayId)=>{
        this.saveCadImg(overlayId, (newCadConfig)=>{
          alert(Yiit.t('New overlay successfully added. Check out it in left sidebar \"Layouts\" tab'));
          this.clearPreviouslyImportedCad();
          this.overlayService.addOverlayWithoutServerUpdate(JSON.parse(newCadConfig));
          this._isCadPlaced$.next(false);
          this.isCadPlacingInProcess = false;
          resolve(newCadConfig);
        });
      },
        (err)=>{
          alert('Oops... An error occurred while creating the overlay'); console.error('err', err);
        });
    }))
  }
}
